import ExtensionResource from 'flow/api/extensions';
import CallRecordsResource from 'flow/api/callrecords';
import ProfilesResource from 'flow/api/profiles';
import Actions from 'flow/actions';
import {
    networkErrorDispatcher,
    httpGetErrorDispatcher,
    spawnAvailableNotification,
    spawnVoiceMessageNotification,
    setSnackbar,
} from 'flow/actions/app-actions';

import { getOwnExtensionKey } from 'flow/selectors/selectors';
import {
    DEFAULT_COUNTRY_CODE,
    getCountryCode,
} from 'flow/utils/extension-utils';
import { userLogEntry } from 'flow/utils/browser-utils';
import { HTTPStatusCodes } from 'flow/utils';
import { getContactDecorations } from 'flow/actions/cti-actions';
import { isAppCti } from 'flow/utils/app-utils';
import { getFocusedActiveCall } from 'flow/selectors/call-selectors';
import { formatNumberToFetchCallRecords } from 'flow/utils/call-records-utils';
import { CALL_RECORD_INCLUDES_DETAIL } from 'flow/utils/call-record-fetch-includes';
import {
    selectExtensionActiveProfile,
    selectExtensionProfiles,
} from 'flow/selectors/ExtensionSelectors';

export const fetchCallRecordsForExtension =
    (
        extensionKey,
        include,
        fromDate = null,
        toDate = null,
        otherPart = null,
        isBetweenAICoworkerAndBPart = false, //this is weird and fragile, to be changed!
    ) =>
    (dispatch, getState) => {
        const { app, customer } = getState();
        const contact = app.getIn(['extension', 'contact']);
        const loggedInCustomer = customer && customer.get(extensionKey);
        const countryCode = loggedInCustomer
            ? getCountryCode(contact, loggedInCustomer)
            : DEFAULT_COUNTRY_CODE;

        const requestParams =
            include && Array.isArray(include) ? include.join() : include;

        const formattedOtherPartForApi =
            otherPart && !isBetweenAICoworkerAndBPart
                ? formatNumberToFetchCallRecords(otherPart, countryCode)
                : null;

        return new Promise((resolve, reject) => {
            CallRecordsResource.getExtensionCallRecords(
                {
                    onResult(json) {
                        if (otherPart) {
                            dispatch(
                                receiveCallRecordsForNumber(
                                    extensionKey,
                                    otherPart,
                                    json,
                                    include,
                                ),
                            );
                        } else {
                            dispatch(
                                receiveCallRecords(extensionKey, json, include),
                            );
                            if (
                                isAppCti(getState()) &&
                                json.records.length > 0
                            ) {
                                const focusedActiveCall =
                                    getFocusedActiveCall(getState());
                                if (!focusedActiveCall) {
                                    dispatch(getContactDecorations(json));
                                }
                            }
                        }
                        resolve();
                    },
                    onError: () => {
                        httpGetErrorDispatcher(dispatch);
                        reject();
                    },
                },
                extensionKey,
                toDate,
                fromDate,
                formattedOtherPartForApi,
                requestParams,
            );
        });
    };

// Solo call-record fetch — defaults to the full include set so the canonical
// record gets enriched once and every downstream consumer (lists, detail page,
// related tabs, voicemail tab) has the data it needs without extra roundtrips.
// Callers can pass a narrower include when there's a reason to (e.g. the CTI
// log fetch in cti-call-log-utils, which only needs `recordings`).
export const fetchCallRecordWithEntityKey =
    (callRecordKey, include = CALL_RECORD_INCLUDES_DETAIL) =>
    (dispatch) => {
        const requestParams = Array.isArray(include) ? include.join() : include;
        return new Promise((resolve, reject) => {
            CallRecordsResource.getCallRecord(
                {
                    onResult(json) {
                        dispatch(
                            receiveCallRecord(callRecordKey, json, include),
                        );
                        resolve(json);
                    },
                    // home-client only calls onError outside the 4xx range, so
                    // without this a client error (403 on a record the caller
                    // may not fetch, 404 on a stale key) settles nothing and
                    // leaves awaiting callers hanging forever. Rejecting without
                    // httpGetErrorDispatcher keeps client errors as quiet as they
                    // were while still ending the wait.
                    onClientError: () => reject(),
                    onError: () => {
                        httpGetErrorDispatcher(dispatch);
                        reject();
                    },
                },
                callRecordKey,
                requestParams,
            );
        });
    };

export const fetchVoiceMessages =
    (extensionKey, ...include) =>
    (dispatch) => {
        ExtensionResource.getVoiceMessages(
            {
                onResult(json) {
                    dispatch(receiveVoiceMessages(extensionKey, json));
                },
                onError: httpGetErrorDispatcher(dispatch),
                onClientError(request) {
                    if (request.status === HTTPStatusCodes.FORBIDDEN_403) {
                        dispatch(
                            receiveVoicemailSettingsForbidden(extensionKey),
                        );
                    }
                },
            },
            extensionKey,
            include.join(),
        );
    };

export const fetchExtensions =
    (...include) =>
    (dispatch, getState) => {
        const hasLiveFlag =
            include.find((flag) => flag === 'live') !== undefined;
        const hasContactFlag =
            include.find((flag) => flag === 'contact') !== undefined;
        const myExtensionKey = getOwnExtensionKey(getState());
        ExtensionResource.getExtensions(
            {
                onResult(json) {
                    dispatch(
                        receiveExtensions(
                            json,
                            hasLiveFlag,
                            hasContactFlag,
                            myExtensionKey,
                        ),
                    );
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            include.join(),
            'desktop',
        );
    };

export const fetchExtension =
    (key, ...include) =>
    (dispatch) => {
        return new Promise((resolve, reject) => {
            ExtensionResource.getExtension(
                {
                    onResult(data) {
                        dispatch(receiveExtension(data));
                        resolve();
                    },
                    onError: (error) => {
                        httpGetErrorDispatcher(dispatch);
                        reject(error);
                    },
                },
                key,
                include.join(),
            );
        });
    };

export const activateExtensionProfile =
    (extensionKey, profile, presentSnackbarMessage = false) =>
    (dispatch, getState) => {
        ExtensionResource.setActiveProfile(
            {
                onResult(data) {
                    const loggedInUserExtensionKey =
                        getOwnExtensionKey(getState());

                    if (extensionKey === loggedInUserExtensionKey) {
                        dispatch(receiveActiveProfile(data));
                    }

                    dispatch(addOrUpdateActiveProfile(extensionKey, data));
                    if (presentSnackbarMessage) {
                        dispatch(
                            setSnackbar({
                                i18nextMessage:
                                    'Settings.Update.Snackbar.Success',
                            }),
                        );
                    }
                },
                onError: networkErrorDispatcher(dispatch),
                onClientError() {
                    if (presentSnackbarMessage) {
                        dispatch(
                            setSnackbar({
                                i18nextMessage: 'Settings.Update.Snackbar.Fail',
                            }),
                        );
                    }
                },
            },
            extensionKey,
            profile,
        );
    };

export const toggleCall = (activeCallEntityKey) => (dispatch, getState) => {
    const activeCallDTO = {
        callTransfer: {
            transferType: 'toggle',
        },
    };

    const extensionKey = getOwnExtensionKey(getState());

    ExtensionResource.updateActiveCall(
        {
            onResult() {
                console.info('Call toggled successfully');
            },
            onError: networkErrorDispatcher(dispatch),
        },
        extensionKey,
        activeCallEntityKey,
        activeCallDTO,
    );
};

export const getCalendar = (extensionKey) => (dispatch) => {
    ExtensionResource.getCalendar(
        {
            onResult(json) {
                dispatch(receiveCalendar(extensionKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        extensionKey,
    );
};

export const updateOpeningHours =
    (extensionKey, openingHours) => async (dispatch) => {
        return new Promise((resolve, reject) => {
            ExtensionResource.setOpeningHours(
                {
                    onResult(json) {
                        dispatch(receiveSetOpeningHours(extensionKey, json));
                        resolve();
                    },
                    onError: () => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                extensionKey,
                openingHours,
            );
        });
    };

const receiveCalendar = (extensionKey, calendar) => ({
    type: Actions.RESULT_EXTENSION_CALENDAR,
    extensionKey: extensionKey,
    data: calendar,
});

export const getPushtypes = (extensionKey) => (dispatch) => {
    return new Promise((resolve, reject) => {
        ExtensionResource.getNotificationScheme(
            {
                onResult(json) {
                    dispatch(receiveNotificationScheme(extensionKey, json));
                    resolve();
                },
                onError: (error) => {
                    httpGetErrorDispatcher(dispatch);
                    reject(error);
                },
                onClientError: (error) => {
                    reject(error);
                },
            },
            extensionKey,
        );
    });
};

export const updatePushtypes =
    (extensionKey, pushtypes, callback) => (dispatch) => {
        return new Promise((resolve, reject) => {
            ExtensionResource.updateNotificationScheme(
                {
                    onResult() {
                        resolve();
                        callback?.();
                    },
                    onError: (error) => {
                        networkErrorDispatcher(dispatch);
                        reject(error);
                    },
                    onClientError: (error) => {
                        reject(error);
                    },
                },
                extensionKey,
                pushtypes,
            );
        });
    };

const receiveNotificationScheme = (extensionKey, json) => ({
    type: Actions.RESULT_NOTIFICATION_SCHEME,
    extensionKey: extensionKey,
    data: json,
});

const receiveActiveProfile = (profile) => {
    return {
        type: Actions.RESULT_NEW_PROFILE,
        data: profile,
    };
};

const receiveCallRecords = (extensionKey, json, include) => ({
    type: Actions.RESULT_CALL_RECORDS_FETCHED,
    scope: { type: 'extension', key: extensionKey },
    data: json,
    include,
});

const receiveCallRecordsForNumber = (
    extensionKey,
    otherPart,
    json,
    include,
) => ({
    type: Actions.RESULT_CALL_RECORDS_FETCHED,
    scope: { type: 'extension', key: extensionKey, phoneNumber: otherPart },
    data: json,
    include,
});

export const extractCallRecordsForNumber = (extensionKey, number) => ({
    type: Actions.EXTRACT_CALL_RECORDS_FOR_NUMBER,
    extensionKey,
    number,
});

const receiveCallRecord = (callRecordKey, json, include) => {
    return {
        type: Actions.RESULT_CALL_RECORD,
        callRecordKey: callRecordKey,
        data: json,
        include,
    };
};

const receiveVoiceMessages = (extensionKey, json) => {
    return {
        type: Actions.RESULT_VOICE_MESSAGES,
        key: extensionKey,
        data: json,
    };
};

const receiveVoicemailSettingsForbidden = (extensionKey) => ({
    type: Actions.RESULT_VOICE_MESSAGES_ERROR,
    key: extensionKey,
});

/**
 * @param {json}    the list of extensions we received from the API
 * @param {live}    (bool) whether we asked for live data or not.
 *                  used to tell the reducer to replace active call lists.
 */
const receiveExtensions = (json, live, contact, myExtensionKey) => {
    return {
        type: Actions.RESULT_EXTENSIONS,
        data: json,
        live,
        contact,
        myExtensionKey,
    };
};

export const receiveExtension = (data) => {
    return {
        type: Actions.RESULT_EXTENSION,
        key: data.key,
        data: data,
        topRouteKey: data.topRouteKey,
    };
};

const receiveMobileSubscription = (key, json) => {
    return {
        type: Actions.RESULT_MOBILE_SUBSCRIPTION,
        key: key,
        data: json,
    };
};

export const createCalendarEvent =
    (extensionKey, calendarEvent) => (dispatch) => {
        ExtensionResource.addEvent(
            {
                onResult(json) {
                    dispatch(receiveAddCalendarEvent(extensionKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            calendarEvent,
        );
    };

export const removeCalendarEvent =
    (extensionKey, calendarEventKey) => (dispatch) => {
        ExtensionResource.deleteEvent(
            {
                onResult(json) {
                    dispatch(receiveRemoveCalendarEvent(extensionKey, json));
                    dispatch(getCalendar(extensionKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            calendarEventKey,
        );
    };

export const updateCalendarEvent =
    (extensionKey, calendarEventKey, calendarEventDTO) => (dispatch) => {
        ExtensionResource.setEvent(
            {
                onResult(json) {
                    dispatch(
                        receiveUpdateCalendarEvent(
                            extensionKey,
                            calendarEventKey,
                            json,
                        ),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            calendarEventKey,
            calendarEventDTO,
        );
    };

/**
 * Update the extension's mobile subscription.
 *
 * @param extensionKey the ExtensionEntityKey
 * @param mobileSubscription the MobileSubscriptionDTO
 */
export const updateMobileSubscription =
    (extensionKey, mobileSubscription) => (dispatch) => {
        ExtensionResource.mobileSubscriptionUpdate(
            {
                onResult(json) {
                    dispatch(receiveMobileSubscription(extensionKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            mobileSubscription,
        );
    };

const receiveSetOpeningHours = (extensionKey, openingHours) => ({
    type: Actions.RESULT_EXTENSION_SET_OPENINGHOURS,
    extensionKey: extensionKey,
    data: openingHours,
});

const receiveAddCalendarEvent = (extensionKey, calenderEventDTO) => ({
    type: Actions.RESULT_EXTENSION_ADD_CALENDAR_EVENT,
    extensionKey: extensionKey,
    data: calenderEventDTO,
});

const receiveUpdateCalendarEvent = (
    extensionKey,
    originalCalendarEventKey,
    calenderEventDTO,
) => ({
    type: Actions.RESULT_EXTENSION_UPDATE_CALENDAR_EVENT,
    extensionKey: extensionKey,
    originalCalendarEventKey: originalCalendarEventKey,
    data: calenderEventDTO,
});

const receiveRemoveCalendarEvent = (extensionKey, calenderEventDTO) => ({
    type: Actions.RESULT_EXTENSION_REMOVE_CALENDAR_EVENT,
    extensionKey: extensionKey,
    data: calenderEventDTO,
});

export const saveActiveProfileOperatorNote =
    (extensionKey, note) => (dispatch, getState) => {
        const activeProfile = selectExtensionActiveProfile(
            getState(),
            extensionKey,
        );

        const plusId = selectExtensionProfiles(getState(), extensionKey)
            ?.find((p) => p.get('key') === activeProfile?.get('key'))
            ?.get('plusId');
        // This looks weird, retrieving plusId from profiles list instead of from the active profile directly.
        // The reason for this is that the active profile might not have the plusId set.

        // Make sure we have a activeProfile
        if (activeProfile) {
            const activeProfileWithNewNote = prepareActiveProfileBeforeSave(
                activeProfile,
                note,
                plusId,
            );
            dispatch(
                activateExtensionProfile(
                    extensionKey,
                    activeProfileWithNewNote,
                    true,
                ),
            );
        } else {
            userLogEntry(
                `User tried to update operator note on ${extensionKey} but could not find active profile in store`,
            );
        }
    };

/**
 * You can't set activeUntil on the 'Available' profile when changing the operator note.
 *
 * Since the plusId is not always set on the activeProfile we send it as an attribute
 *
 * @param activeProfile the active profile to update
 * @param note the status message to update
 * @param plusId the plusId of the active profile
 */
const prepareActiveProfileBeforeSave = (activeProfile, note, plusId) => {
    if (plusId === 0) {
        return activeProfile
            .set('activeUntil', null)
            .set('operatorNote', note || '');
    } else {
        return activeProfile.set('operatorNote', note || '');
    }
};

export const removeVoiceMessage =
    (extensionEntityKey, voiceMessageUniqueId, callRecordKey) => (dispatch) => {
        ExtensionResource.removeVoiceMessage(
            {
                onResult() {
                    dispatch(
                        updateVoiceMessageState(
                            extensionEntityKey,
                            voiceMessageUniqueId,
                            callRecordKey,
                        ),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionEntityKey,
            voiceMessageUniqueId,
        );
    };

export const removeAllVoiceMessages = (entityKey) => (dispatch) => {
    ExtensionResource.removeAllVoiceMessages(
        {
            onResult(/*json*/) {
                dispatch(receiveVoicemailDeleteAll(entityKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        entityKey,
    );
};

const receiveVoicemailDeleteAll = (entityKey) => ({
    type: Actions.RESULT_VOICEMAILS_DELETE_ALL,
    extensionKey: entityKey,
});

const updateVoiceMessageState = (
    extensionKey,
    voiceMessageUniqueId,
    callRecordKey,
) => ({
    type: Actions.UPDATE_VOICEMESSAGES_AFTER_DELETE,
    extensionKey,
    voiceMessageUniqueId,
    callRecordKey,
});

export const createPresenceNotification =
    (extensionKey, targetExtensionDTO) => (dispatch) => {
        const targetExtensionKey = targetExtensionDTO.get('key');
        ExtensionResource.createPresenceNotification(
            {
                onResult(/* json */) {
                    dispatch(
                        receiveCreatePresenceNotification(
                            extensionKey,
                            targetExtensionDTO,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            targetExtensionKey,
        );
    };

export const deletePresenceNotification =
    (extensionKey, targetExtensionDTO) => (dispatch) => {
        const targetExtensionKey = targetExtensionDTO.get('key');
        ExtensionResource.deletePresenceNotification(
            {
                onResult(/* json */) {
                    dispatch(
                        receiveDeletePresenceNotification(
                            extensionKey,
                            targetExtensionDTO,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            targetExtensionKey,
        );
    };

export const handlePresenceNotification =
    (notificationActive, loggedInUserKey, targetExtensionDTO) => (dispatch) => {
        if (notificationActive) {
            dispatch(
                createPresenceNotification(loggedInUserKey, targetExtensionDTO),
            );
        } else {
            dispatch(
                deletePresenceNotification(loggedInUserKey, targetExtensionDTO),
            );
        }
    };

export const fetchPresenceNotifications =
    (extensionKey, ...include) =>
    (dispatch) => {
        ExtensionResource.getPresenceNotifications(
            {
                onResult(json) {
                    dispatch(
                        receiveFetchPresenceNotifications(extensionKey, json),
                    );
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            extensionKey,
            include.join(),
        );
    };

export const sendNumberVerification =
    (extensionKey, mobileNumberToVerify) => (dispatch) => {
        ExtensionResource.sendNumberVerification(
            {
                onResult(/* json */) {},
                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            mobileNumberToVerify,
        );
    };

export const getMobileStatus = (extensionKey) => (dispatch) => {
    ExtensionResource.getMobileStatus(
        {
            onResult(json) {
                dispatch(recevieMobileStatus(extensionKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        extensionKey,
    );
};

export const fetchMobileSubscription =
    (extensionKey, ...include) =>
    async (dispatch) => {
        return new Promise((resolve, reject) => {
            ExtensionResource.getMobileSubscription(
                {
                    onResult(json) {
                        dispatch(replaceMobileSubscription(extensionKey, json));
                        resolve();
                    },
                    onError: (error) => {
                        httpGetErrorDispatcher(dispatch);
                        reject(error);
                    },
                },
                extensionKey,
                include.join(),
            );
        });
    };

export const addOrUpdateActiveCall = (extensionKey, call) => (dispatch) => {
    dispatch({
        type: Actions.EXTENSION_UPDATE_ACTIVE_CALL,
        extensionKey: extensionKey,
        data: call,
    });
};
export const addOrUpdateActiveProfile = (extensionKey, activeProfile) => ({
    type: Actions.EXTENSION_UPDATE_ACTIVE_PROFILE,
    extensionKey: extensionKey,
    data: activeProfile,
});

export const removeActiveCall = (extensionKey, activeCall) => ({
    type: Actions.EXTENSION_REMOVE_ACTIVE_CALL,
    extensionKey: extensionKey,
    data: activeCall,
});

const receiveFetchPresenceNotifications = (extensionKey, extensionDTOs) => ({
    type: Actions.RESULT_FETCH_PRESENCE_NOTIFICATIONS,
    extensionKey: extensionKey,
    data: extensionDTOs,
});

export const receiveCreatePresenceNotification = (
    extensionKey,
    targetExtensionDTO,
) => ({
    type: Actions.RESULT_CREATE_PRESENCE_NOTIFICATION,
    extensionKey: extensionKey,
    data: targetExtensionDTO,
});

export const receiveTopPresenceNotification =
    (extensionKey, targetExtensionDTO, silent = true) =>
    (dispatch) => {
        dispatch(
            receiveDeletePresenceNotification(extensionKey, targetExtensionDTO),
        );

        if (!silent) {
            const contact = targetExtensionDTO.get('contact');
            dispatch(spawnAvailableNotification(contact));
        }
    };

export const receiveTopVoiceMessage =
    (extensionKey, voiceMessageDTO, silent = true) =>
    (dispatch) => {
        dispatch(fetchVoiceMessages(extensionKey, 'contact'));

        if (!silent) {
            dispatch(spawnVoiceMessageNotification(voiceMessageDTO));
        }
    };

export const setVoiceMessageRead =
    (extensionKey, callRecordKey, voiceMessageUniqueId) => (dispatch) => {
        dispatch(
            receiveSetVoiceMessageRead(
                extensionKey,
                callRecordKey,
                voiceMessageUniqueId,
            ),
        );
    };

const receiveSetVoiceMessageRead = (
    extensionKey,
    callRecordKey,
    voiceMessageUniqueId,
) => ({
    type: Actions.SET_VOICEMESSAGE_READ,
    extensionKey,
    callRecordKey,
    voiceMessageUniqueId,
});

const receiveDeletePresenceNotification = (
    extensionKey,
    targetExtensionDTO,
) => ({
    type: Actions.RESULT_DELETE_PRESENCE_NOTIFICATION,
    extensionKey: extensionKey,
    data: targetExtensionDTO,
});

const recevieMobileStatus = (extensionKey, json) => ({
    type: Actions.RESULT_EXTENSION_MOBILE_STATUS,
    extensionKey: extensionKey,
    data: json,
});

const replaceMobileSubscription = (extensionKey, json) => ({
    type: Actions.RESULT_EXTENSION_MOBILE_SUBSCRIPTION,
    extensionKey: extensionKey,
    data: json,
});

export const setCallListFilter = (filter) => ({
    type: Actions.SET_CALL_LIST_FILTER,
    filter: filter,
});

export const clearExtensionsActiveCalls = () => ({
    type: Actions.CLEAR_EXTENSIONS_ACTIVE_CALLS,
});

export const canComonitor = (extensionKey) => (dispatch) => {
    return new Promise((resolve) => {
        ExtensionResource.canMonitor(
            {
                onResult: (json) => {
                    resolve(json);
                },
                onError: () => {
                    httpGetErrorDispatcher(dispatch);
                    resolve(false);
                },
                onClientError: () => {
                    resolve(false);
                },
            },
            extensionKey,
        );
    });
};

export const setPinCode = (extensionKey, callKey, pinCode) => ({
    type: Actions.EXTENSION_UPDATE_ACTIVE_CALL_PIN_CODE,
    extensionKey,
    callKey,
    pinCode,
});

export const fetchNextScheduledProfiles = (operatorKey) => (dispatch) => {
    const keyApiFriendly = operatorKey === 'all' ? undefined : operatorKey;
    ProfilesResource.getNextScheduledProfiles(
        {
            onResult(json) {
                dispatch(receiveNextScheduledProfiles(json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        keyApiFriendly,
        'desktop',
    );
};

export const receiveNextScheduledProfiles = (profiles) => ({
    type: Actions.RESULT_EXTENSIONS_NEXT_SCHEDULED_PROFILES,
    data: profiles,
});
